import { Component } from 'react';

/**
 * Last line of defence against a blank page.
 *
 * React unmounts the whole tree when a render throws, so without a boundary the
 * user gets a black screen with nothing in it - no message, no navigation, no
 * way to sign out. The console has the stack, but nobody opens the console.
 *
 * Deliberately plain: no hooks, no context, no imports beyond React, because
 * this has to render even when the thing that broke is the app state itself.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the component stack: the message alone rarely says which view broke.
    console.error('Pulse crashed while rendering', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24 }}>
        <div className="card" style={{ maxWidth: 520 }}>
          <div className="card-body" style={{ display: 'grid', gap: 12 }}>
            <h1 style={{ fontSize: 17, margin: 0 }}>Something broke on this screen</h1>
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
              {error.message || String(error)}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn" onClick={() => window.location.reload()}>
                Reload
              </button>
              {/*
                Clears the stored session as well as reloading. If the crash is
                caused by the signed-in state itself, a plain reload just
                reproduces it, and the user has no other way out.
              */}
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  try {
                    Object.keys(localStorage)
                      .filter((k) => k.startsWith('pulse.'))
                      .forEach((k) => localStorage.removeItem(k));
                  } catch { /* private mode: nothing to clear */ }
                  window.location.href = '/#/login';
                  window.location.reload();
                }}
              >
                Sign out and reload
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
